const REFERENCE_HEIGHT_IN = 69;
const TALL_HEIGHT_IN = 74;
const SHORT_HEIGHT_IN = 64;
const TALL_ADJUSTMENT = 0.35;
const SHORT_ADJUSTMENT = -0.25;
const USUAL_CLEAR_GAP = 1.25;
const BOUNDARY_WINDOW = 0.2;
const REASON_THRESHOLD = 0.2;
const FALLBACK_BAND_WIDTH = 25;

export const BUILD_ADJUSTMENTS = Object.freeze({ slim: -0.35, average: 0, fuller: 0.45 });
export const THIGH_ADJUSTMENTS = Object.freeze({ slim: -0.15, average: 0, fuller: 0.5 });
export const FIT_ADJUSTMENTS = Object.freeze({ true: 0, small: 0.5, large: -0.5, boxy: -0.25 });

export const PREFERRED_FIT_ADJUSTMENTS = Object.freeze({ fitted: -0.4, true: 0, oversized: 0.7, slim: -0.3, regular: 0, baggy: 0.6 });
export const SHOULDER_ADJUSTMENTS = Object.freeze({ narrow: -0.15, average: 0, broad: 0.35 });

export const LIMITS = Object.freeze({
  heightIn: { min: 54, max: 86 },
  heightCm: { min: 137, max: 218 },
  weightLb: { min: 80, max: 400 },
  weightKg: { min: 36, max: 181 },
  waistIn: { min: 22, max: 56 },
  waistCm: { min: 56, max: 142 },
});

// Waist bands (inches) for letter sizes; a bottom's waist answer is placed on these like weight is on the chart.
export const WAIST_BANDS = Object.freeze({
  '2xs': [24, 26],
  xs: [26, 28],
  s: [28, 31],
  m: [31, 34],
  l: [34, 37],
  xl: [37, 40],
  '2xl': [40, 43],
  '3xl': [43, 46],
  '4xl': [46, 49],
});

export const FIT_CATEGORIES = Object.freeze(['top', 'bottom', 'headwear', 'one_size']);
// Kept in sync with snippets/fit-category.liquid (tests/size-quiz-category.test.mjs checks both lists).
export const CATEGORY_KEYWORDS = Object.freeze({
  headwear: Object.freeze(['beanie', 'skully', 'skullcap', 'hat']),
  top: Object.freeze(['tee', 'tank', 'hoodie', 'crewneck', 'cutsleeve', 'cutoff', 'jersey', 'shirt']),
  bottom: Object.freeze(['jort', 'short', 'pant', 'jean', 'sweat', 'trouser']),
});

// Every step auto-advances: single choices on tap, height + weight once both are picked.
export const QUIZ_STEPS = Object.freeze(['usual', 'measurements', 'preferredFit', 'result']);
export const PREFERRED_FIT_KEYS = Object.freeze(['fitted', 'regular', 'baggy']);
export const CATEGORY_STEPS = Object.freeze({ top: QUIZ_STEPS, bottom: QUIZ_STEPS, general: QUIZ_STEPS });

function tokens(text) {
  return String(text ?? '')
    .toLowerCase()
    .replace(/cut[\s-]+sleeve/g, 'cutsleeve')
    .replace(/cut[\s-]+off/g, 'cutoff')
    .replace(/skull[\s-]+cap/g, 'skullcap')
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function tokenCategory(token) {
  for (const category of ['headwear', 'top', 'bottom']) {
    const hit = CATEGORY_KEYWORDS[category].some((keyword) => (keyword === 'shirt' ? token.includes(keyword) : token.startsWith(keyword)));
    if (hit) return category;
  }
  return '';
}

// The last garment word wins: "cutoff jorts" is a bottom, "short sleeve tee" a top, "sweatshirt" a top.
export function categoryFromText(text) {
  let found = '';
  for (const token of tokens(text)) found = tokenCategory(token) || found;
  return found;
}

export function detectFitCategory({ override = '', metafield = '', type = '', tags = [], title = '' } = {}) {
  const exact = (value) => {
    const key = String(value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
    return FIT_CATEGORIES.includes(key) ? key : '';
  };
  const tagList = Array.isArray(tags) ? tags : String(tags || '').split(',');
  return (
    exact(override) ||
    exact(metafield) ||
    categoryFromText(type) ||
    categoryFromText(tagList.join(' ')) ||
    categoryFromText(title) ||
    'general'
  );
}

export function stepsFor(category) {
  return CATEGORY_STEPS[category] || CATEGORY_STEPS.general;
}

export function showsQuiz(category) {
  return category !== 'headwear' && category !== 'one_size';
}

export const ADVANCE_DELAY = 250;

export function autoAdvances(step) {
  return step !== 'result';
}

// Step position for the quiz dialog. choose() moves on after `delay` (at once with reduced motion)
// once the step is answered; there is no Continue except on a step revisited with Back, where
// re-picking the same value would not fire anything.
export function createStepFlow({
  steps,
  delay = ADVANCE_DELAY,
  reducedMotion = () => false,
  schedule = (fn, ms) => setTimeout(fn, ms),
  cancel = (id) => clearTimeout(id),
  onChange = () => {},
} = {}) {
  let index = 0;
  let furthest = 0;
  let timer = null;
  const last = steps.length - 1;
  const flow = {
    get index() {
      return index;
    },
    get step() {
      return steps[index];
    },
    get pending() {
      return timer !== null;
    },
    get showContinue() {
      return index < furthest;
    },
    go(target) {
      if (timer !== null) cancel(timer);
      timer = null;
      index = Math.max(0, Math.min(last, target));
      furthest = Math.max(furthest, index);
      onChange(index, steps[index]);
    },
    reset() {
      furthest = 0;
      flow.go(0);
    },
    choose(stepName) {
      const own = steps.indexOf(stepName);
      if (own !== index || own >= last || !autoAdvances(stepName)) return false;
      if (timer !== null) cancel(timer);
      if (reducedMotion()) {
        timer = null;
        flow.go(own + 1);
      } else {
        timer = schedule(() => {
          timer = null;
          flow.go(own + 1);
        }, delay);
      }
      return true;
    },
    submit(stepName, valid) {
      if (steps.indexOf(stepName) !== index || !valid || index >= last) return false;
      flow.go(index + 1);
      return true;
    },
  };
  return flow;
}

const CM_PER_IN = 2.54;
const LB_PER_KG = 2.2046226218;

export function cmToIn(cm) {
  return Number(cm) / CM_PER_IN;
}

export function inToCm(inches) {
  return Number(inches) * CM_PER_IN;
}

export function kgToLb(kg) {
  return Number(kg) * LB_PER_KG;
}

export function lbToKg(lb) {
  return Number(lb) / LB_PER_KG;
}

export function ftInToIn(feet, inches = 0) {
  return Number(feet || 0) * 12 + Number(inches || 0);
}

export function inToFtIn(totalInches) {
  const rounded = Math.round(Number(totalInches));
  return { feet: Math.floor(rounded / 12), inches: rounded % 12 };
}

export const SIZE_ALIASES = Object.freeze({
  extrasmall: 'xs',
  xsmall: 'xs',
  small: 's',
  sm: 's',
  medium: 'm',
  med: 'm',
  md: 'm',
  large: 'l',
  lg: 'l',
  lrg: 'l',
  extralarge: 'xl',
  xlarge: 'xl',
  xxlarge: '2xl',
  xxxlarge: '3xl',
  xxxxl: '4xl',
  xxxl: '3xl',
  xxl: '2xl',
  xxs: '2xs',
});

export function normalizeSize(value) {
  const key = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s.\-_]+/g, '');
  return lookup(SIZE_ALIASES, key, key);
}

export function parseChart(text) {
  const chart = [];
  const seen = new Set();
  for (const rawLine of String(text ?? '').split(/\r?\n|\r|<br\s*\/?>/i)) {
    const parts = rawLine.split(',').map((part) => part.trim());
    if (parts.length < 3 || !parts[0]) continue;
    const min = Number(parts[1]);
    const max = Number(parts[2]);
    if (!Number.isFinite(min) || !Number.isFinite(max) || max < min) continue;
    const key = normalizeSize(parts[0]);
    if (seen.has(key)) continue;
    seen.add(key);
    chart.push({ size: parts[0], key, min, max });
  }
  return chart;
}

function median(values) {
  if (!values.length) return FALLBACK_BAND_WIDTH;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function bandEdges(chart) {
  const lowers = [];
  chart.forEach((band, index) => {
    lowers.push(index === 0 ? band.min : Math.max(band.min, lowers[index - 1]));
  });
  const uppers = chart.map((band, index) =>
    index < chart.length - 1 ? Math.max(lowers[index + 1], lowers[index] + 1) : Math.max(band.max, lowers[index] + 1)
  );
  const interior = uppers.slice(1, -1).map((upper, offset) => upper - lowers[offset + 1]);
  const typical = median(interior.length ? interior : uppers.map((upper, index) => upper - lowers[index]));
  const last = chart.length - 1;
  if (uppers[0] - lowers[0] > typical * 1.5) lowers[0] = uppers[0] - typical;
  if (last > 0 && uppers[last] - lowers[last] > typical * 1.5) uppers[last] = lowers[last] + typical;
  return { lowers, uppers };
}

export function weightPosition(weight, chart) {
  const { lowers, uppers } = bandEdges(chart);
  const last = chart.length - 1;
  if (weight < lowers[0]) return -0.5 + (weight - lowers[0]) / (uppers[0] - lowers[0]);
  if (weight >= uppers[last]) return last + 0.5 + (weight - uppers[last]) / (uppers[last] - lowers[last]);
  let index = 0;
  while (index < last && weight >= uppers[index]) index += 1;
  return index - 0.5 + (weight - lowers[index]) / (uppers[index] - lowers[index]);
}

export function adjustedWeight(weightLb, heightIn) {
  return weightLb * (REFERENCE_HEIGHT_IN / heightIn) ** 2;
}

export function heightAdjustment(heightIn) {
  if (heightIn > TALL_HEIGHT_IN) return TALL_ADJUSTMENT;
  if (heightIn < SHORT_HEIGHT_IN) return SHORT_ADJUSTMENT;
  return 0;
}

function lookup(table, key, fallback = 0) {
  return Object.prototype.hasOwnProperty.call(table, key) ? table[key] : fallback;
}

function round2(value) {
  return Math.round(value * 100) / 100;
}

function resolveOffered(chart, offeredSizes) {
  const labels = new Map();
  for (const label of offeredSizes || []) {
    const key = normalizeSize(label);
    if (key && !labels.has(key)) labels.set(key, String(label));
  }
  const offered = [];
  chart.forEach((band, index) => {
    if (!offeredSizes || labels.has(band.key)) offered.push({ index, size: labels.get(band.key) ?? band.size });
  });
  return offered.length ? offered : chart.map((band, index) => ({ index, size: band.size }));
}

function nearestOffered(offered, score) {
  let best = offered[0];
  for (const candidate of offered) {
    const distance = Math.abs(candidate.index - score);
    const bestDistance = Math.abs(best.index - score);
    if (distance < bestDistance || (distance === bestDistance && candidate.index > best.index)) best = candidate;
  }
  return best;
}

// Places a waist (inches) on the chart's rows: letter sizes use WAIST_BANDS, numeric sizes are the waist itself.
export function waistPosition(waistIn, chart) {
  const waist = Number(waistIn);
  if (!within(waist, LIMITS.waistIn) || chart.length < 2) return null;
  const bands = chart.map((band) => {
    const numeric = Number(band.key);
    if (Number.isFinite(numeric) && numeric >= LIMITS.waistIn.min && numeric <= LIMITS.waistIn.max) return { min: numeric - 1, max: numeric + 1 };
    const range = WAIST_BANDS[band.key];
    return range ? { min: range[0], max: range[1] } : null;
  });
  if (bands.some((band) => !band)) return null;
  return weightPosition(waist, bands);
}

export function recommendSize(input = {}, chartInput = [], options = {}) {
  const chart = typeof chartInput === 'string' ? parseChart(chartInput) : chartInput;
  if (!chart.length) return null;

  const offered = resolveOffered(chart, options.offeredSizes);
  const heightIn = Number(input.heightIn);
  const weightLb = Number(input.weightLb);
  const hasMeasurements = heightIn > 0 && weightLb > 0;
  const usualKey = normalizeSize(input.usualSize);
  const usualIndex = usualKey ? chart.findIndex((band) => band.key === usualKey) : -1;
  const usualOffered = usualIndex >= 0 && offered.some((entry) => entry.index === usualIndex);

  const contributions = {
    height: hasMeasurements ? heightAdjustment(heightIn) : 0,
    build: lookup(BUILD_ADJUSTMENTS, input.build),
    thighs: lookup(THIGH_ADJUSTMENTS, input.thighs),
    shoulders: lookup(SHOULDER_ADJUSTMENTS, input.shoulders),
    waist: 0,
    fit: lookup(FIT_ADJUSTMENTS, String(input.fit ?? 'true')),
    preferredFit: lookup(PREFERRED_FIT_ADJUSTMENTS, String(input.preferredFit ?? '')),
    usual: 0,
  };

  let base;
  if (hasMeasurements) base = weightPosition(adjustedWeight(weightLb, heightIn), chart);
  else if (usualOffered) base = usualIndex;
  else base = (chart.length - 1) / 2;
  // A typed waist outweighs weight for bottoms; without height/weight it stands alone.
  const waistAt = waistPosition(input.waistIn, chart);
  if (waistAt !== null) contributions.waist = hasMeasurements ? (waistAt - base) * 0.6 : waistAt - base;
  const measured = base + contributions.height + contributions.build + contributions.thighs + contributions.shoulders + contributions.waist;

  // A stated usual size wins unless the measurements point more than about a size away.
  let score = measured;
  let usualMode = '';
  const usualGap = measured - usualIndex;
  if (usualOffered && hasMeasurements) {
    if (Math.abs(usualGap) < USUAL_CLEAR_GAP) {
      usualMode = 'kept';
      score = usualIndex;
      contributions.usual = -usualGap;
      contributions.height = 0;
      contributions.build = 0;
      contributions.thighs = 0;
      contributions.shoulders = 0;
      contributions.waist = 0;
    } else {
      usualMode = 'overridden';
      score = usualIndex + Math.sign(usualGap) * (Math.abs(usualGap) - (USUAL_CLEAR_GAP - 1));
    }
  }
  score += contributions.fit + contributions.preferredFit;

  const chosen = nearestOffered(offered, score);
  const offset = score - chosen.index;
  const neighbourOf = (direction) =>
    direction === 'up'
      ? offered.find((entry) => entry.index > chosen.index)
      : [...offered].reverse().find((entry) => entry.index < chosen.index);
  let alternative = null;
  if (usualMode === 'kept') {
    if (Math.abs(usualGap) >= 0.5) {
      const direction = usualGap > 0 ? 'up' : 'down';
      const neighbour = neighbourOf(direction);
      if (neighbour) alternative = { size: neighbour.size, index: neighbour.index, direction };
    }
  } else if (Math.abs(offset) > 0.5 - BOUNDARY_WINDOW && Math.abs(offset) < 0.5 + BOUNDARY_WINDOW) {
    const direction = offset > 0 ? 'up' : 'down';
    const neighbour = neighbourOf(direction);
    if (neighbour) alternative = { size: neighbour.size, index: neighbour.index, direction };
  }
  // A fit preference too small to move the size (or absorbed by the garment's cut) still offers the next size that way.
  const preferredDirection = Math.sign(contributions.preferredFit);
  const preferenceAbsorbed = preferredDirection !== 0 && nearestOffered(offered, score - contributions.preferredFit).index === chosen.index;
  if (preferenceAbsorbed && alternative?.direction !== (preferredDirection > 0 ? 'up' : 'down')) {
    const direction = preferredDirection > 0 ? 'up' : 'down';
    const neighbour = neighbourOf(direction);
    if (neighbour) alternative = { size: neighbour.size, index: neighbour.index, direction };
  }

  let reason = hasMeasurements ? 'measurements' : usualOffered ? 'usual' : 'measurements';
  let reasonDirection = 0;
  if (usualMode === 'kept') {
    reason = 'usual';
    if (chosen.index !== usualIndex) {
      if (contributions.preferredFit) reason = 'preferredFit';
      else if (contributions.fit) reason = 'fit';
    } else if (preferenceAbsorbed && Math.sign(contributions.fit) === -preferredDirection) {
      reason = 'fit';
    }
    reasonDirection = Math.sign(contributions[reason] || 0);
  } else if (usualMode === 'overridden' && chosen.index === usualIndex) {
    reason = contributions.preferredFit ? 'preferredFit' : contributions.fit ? 'fit' : 'usual';
    reasonDirection = Math.sign(contributions[reason] || 0);
  } else if (usualMode === 'overridden') {
    reason = 'usual_override';
    reasonDirection = Math.sign(usualGap);
  } else {
    let strongest = REASON_THRESHOLD - 1e-9;
    for (const key of ['waist', 'thighs', 'build', 'shoulders', 'height', 'preferredFit', 'fit', 'usual']) {
      const magnitude = Math.abs(contributions[key]);
      if (magnitude > strongest) {
        strongest = magnitude;
        reason = key;
      }
    }
    reasonDirection = Math.sign(contributions[reason] || 0);
  }
  const baseConfidence = hasMeasurements ? 1 : usualOffered ? 0.6 : 0.2;
  const confidence = round2(Math.max(0, Math.min(1, baseConfidence * (1 - Math.min(Math.abs(offset), 1) * 0.8))));

  return {
    size: chosen.size,
    index: chosen.index,
    score: round2(score),
    alternative,
    reason,
    reasonDirection: reasonDirection > 0 ? 'up' : reasonDirection < 0 ? 'down' : 'none',
    confidence,
    contributions: Object.fromEntries(Object.entries(contributions).map(([key, value]) => [key, round2(value)])),
  };
}

function within(value, { min, max }) {
  return Number.isFinite(value) && value >= min && value <= max;
}

export function isValidHeight(value, unit = 'in') {
  return within(Number(value), unit === 'cm' ? LIMITS.heightCm : LIMITS.heightIn);
}

export function isValidWeight(value, unit = 'lb') {
  return within(Number(value), unit === 'kg' ? LIMITS.weightKg : LIMITS.weightLb);
}

const api = {
  recommendSize,
  parseChart,
  normalizeSize,
  detectFitCategory,
  categoryFromText,
  stepsFor,
  showsQuiz,
  autoAdvances,
  createStepFlow,
  ADVANCE_DELAY,
  waistPosition,
  weightPosition,
  adjustedWeight,
  heightAdjustment,
  isValidHeight,
  isValidWeight,
  cmToIn,
  inToCm,
  kgToLb,
  lbToKg,
  ftInToIn,
  inToFtIn,
  LIMITS,
};

if (typeof window !== 'undefined') {
  window.ConvertSizeQuiz = api;
  if (typeof document !== 'undefined') document.dispatchEvent(new CustomEvent('sizequiz:core-ready'));
}

export default api;
